/*
 * Copyright (c) 2025, Wultra s.r.o. (www.wultra.com).
 *
 * All rights reserved. This source code can be used only for purposes specified
 * by the given license contract signed by the rightful deputy of Wultra s.r.o.
 * This source code can be used only by the owner of the license.
 *
 * Any disputes arising in respect of this agreement (license) shall be brought
 * before the Municipal Court of Prague.
 */

import clsx from "clsx"
import type { LucideIcon } from "lucide-react"
import { ShieldAlertIcon, TabletSmartphoneIcon, TriangleAlertIcon } from "lucide-react"
import { observer } from "mobx-react-lite"
import { useRef, useState } from "react"

import { randomBetween } from "../../utils/random-between"
import { useAnimateValueEffect } from "../../utils/use-animate-value-effect"
import { Card, CardContent, CardHeader, CardTitle } from "../primitives/card"

import styles from "./dashboard-view.module.css"

/**
 * Label and icon for each dashboard tile.
 *
 * Each key matches a tone class in `dashboard-view.module.css`, shared with the table's count styling.
 * Tiles retain their background fill when the count is zero.
 */
const Tile = {
    insecure: { label: "Insecure", Icon: TriangleAlertIcon },
    outdated: { label: "Outdated", Icon: ShieldAlertIcon },
    active: { label: "Active", Icon: TabletSmartphoneIcon }
} as const satisfies Record<string, { label: string; Icon: LucideIcon }>

interface Props {
    count: number
    kind: keyof typeof Tile
}

/**
 * Displays a summary tile with a label, icon, and animated count.
 *
 * The count starts at zero and animates to the current value as application
 * counts arrive. Tiles are informational; the corresponding table counts
 * provide links to the device lists.
 *
 * The `@xl/dashboard:` variants respond to the container named `dashboard`
 * by `DashboardView`. Naming it explicitly avoids measuring `CardHeader`,
 * which also establishes a container.
 */
export const DashboardTile = observer(({ count, kind }: Props) => {
    const ref = useRef<HTMLSpanElement>(null)

    // Keep the duration stable across renders so a re-render without a new count
    // doesn't restart the animation.
    const [duration] = useState(randomDuration)
    useAnimateValueEffect(ref, duration, count)

    const { label, Icon } = Tile[kind]

    return (
        // Override the card's default background and text colours with the tile tone.
        // Reduce the default gap to keep the tile compact, and remove the ring because
        // the solid fill provides sufficient visual separation.
        <Card size="sm" className={clsx("gap-1 bg-(--tone-fill) text-(--tone-fill-foreground) ring-0", styles[kind])}>
            <CardHeader>
                <CardTitle className="text-xs uppercase @xl/dashboard:text-sm">{label}</CardTitle>
            </CardHeader>
            <CardContent className="flex-row items-center justify-between gap-1 @xl/dashboard:gap-4">
                <Icon className="size-5 opacity-50 @xl/dashboard:size-8" strokeWidth={2.25} />
                <span ref={ref} className="text-3xl/none font-bold tabular-nums @xl/dashboard:text-4xl/none" />
            </CardContent>
        </Card>
    )
})

function randomDuration() {
    return randomBetween(1_000, 1_600)
}
